import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FailedPipelineRun } from '../../hooks/useFailedPipelines';
import { useUpdateListenerAudio } from '../../hooks/useListenerAudio';
import { use2026TagGroups } from '../../hooks/useTagGroups';

interface EditPipelineModalProps {
  run: FailedPipelineRun;
  onClose: () => void;
}

interface FormValues {
  ai_title: string;
  ai_summary: string;
  excerpt: string;
  transcription: string;
}

const EditPipelineModal: React.FC<EditPipelineModalProps> = ({ run, onClose }) => {
  const { mutateAsync: updateListenerAudio, isLoading: isSaving } = useUpdateListenerAudio();
  const { allTags } = use2026TagGroups();
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(run.topic_tag_ids || []);

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      ai_title: run.listener_audio_ai_title || '',
      ai_summary: run.listener_audio_ai_summary || '',
      excerpt: run.listener_audio_excerpt || '',
      transcription: run.listener_audio_transcription || '',
    },
  });

  useEffect(() => {
    reset({
      ai_title: run.listener_audio_ai_title || '',
      ai_summary: run.listener_audio_ai_summary || '',
      excerpt: run.listener_audio_excerpt || '',
      transcription: run.listener_audio_transcription || '',
    });
    setSelectedTagIds(run.topic_tag_ids || []);
  }, [run, reset]);

  const tagOptions = useMemo(() => allTags || [], [allTags]);

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const onSubmit = async (data: FormValues) => {
    if (!run.listener_role_id) return;

    await updateListenerAudio({
      listenerId: run.listener_role_id,
      audioId: run.listener_audio_id,
      payload: {
        topic_tag_ids: selectedTagIds,
        ai_title: data.ai_title || undefined,
        ai_summary: data.ai_summary || undefined,
        excerpt: data.excerpt || undefined,
        transcription: data.transcription || undefined,
      },
    });
    onClose();
  };

  const failedSteps = run.steps.filter((s) => s.status === 'failed');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Edit Pipeline Run #{run.id}</h2>
          <button onClick={onClose} className="text-2xl leading-none text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="mb-4 rounded bg-red-50 p-3 text-sm">
          <p className="mb-1 font-medium text-red-800">Error Details</p>
          <p className="text-red-700">{run.error_message || 'No error message'}</p>
          {failedSteps.length > 0 && (
            <div className="mt-2">
              <p className="font-medium text-red-800">Failed Steps:</p>
              {failedSteps.map((step) => (
                <p key={step.id} className="text-red-700">
                  <span className="font-medium">{step.step_name}</span>: {step.error_message || 'No message'}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="mb-3 rounded bg-gray-50 p-3 text-sm">
          <p>
            <span className="font-medium">Listener Audio ID:</span> {run.listener_audio_id}
          </p>
          <p>
            <span className="font-medium">Listener:</span> {run.listener_name || 'Unknown'} (Role ID:{' '}
            {run.listener_role_id})
          </p>
          <p>
            <span className="font-medium">Created:</span>{' '}
            {(() => {
              // Backend timestamps may be returned without a timezone (e.g. `2026-03-20T07:30:00`).
              // Assume UTC when missing an explicit timezone suffix.
              const normalized = /Z$|[+-]\d\d:\d\d$/.test(run.created_at) ? run.created_at : `${run.created_at}Z`;
              return new Date(normalized).toLocaleString();
            })()}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">AI Title</label>
            <input
              {...register('ai_title')}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">Excerpt</label>
            <textarea
              {...register('excerpt')}
              rows={2}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">AI Summary</label>
            <textarea
              {...register('ai_summary')}
              rows={3}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">Transcription</label>
            <textarea
              {...register('transcription')}
              rows={4}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Topic Tags</label>
            <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded border border-gray-200 p-2">
              {tagOptions.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    selectedTagIds.includes(tag.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
              {tagOptions.length === 0 && <span className="text-xs text-gray-400">No tags available</span>}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !run.listener_role_id}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPipelineModal;
